import React from 'react';
import { Link } from 'react-router-dom';
import { FooterLogo } from './Logo';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Shield,
  Heart,
  Users,
  Calendar
} from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-clinic-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <FooterLogo className="mb-6" />
            <p className="text-clinic-gray-300 mb-6 leading-relaxed">
              Providing comprehensive psychiatric services through secure telehealth and onsite consultations. 
              Serving patients at home and in licensed care facilities nationwide.
            </p>
            
            {/* Trust Indicators */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-green-400" />
                <span className="text-sm text-clinic-gray-300">HIPAA Compliant</span>
              </div>
              <div className="flex items-center space-x-2">
                <Heart className="w-4 h-4 text-red-400" />
                <span className="text-sm text-clinic-gray-300">Licensed</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              <a href="#" className="text-clinic-gray-400 hover:text-white transition-colors duration-200">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-clinic-gray-400 hover:text-white transition-colors duration-200">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-clinic-gray-400 hover:text-white transition-colors duration-200">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="text-clinic-gray-400 hover:text-white transition-colors duration-200">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Services</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/for-patients" className="text-clinic-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  For Patients
                </Link>
              </li>
              <li>
                <Link to="/for-facilities" className="text-clinic-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  For Facilities
                </Link>
              </li>
              <li>
                <Link to="/providers" className="text-clinic-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  Find Providers
                </Link>
              </li>
              <li>
                <Link to="/booking/telehealth" className="text-clinic-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Appointment
                </Link>
              </li>
              <li>
                <a href="/crisis-support" className="text-clinic-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                  <Phone className="w-4 h-4 mr-2" />
                  Crisis Support
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Company</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-clinic-gray-300 hover:text-white transition-colors duration-200">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-clinic-gray-300 hover:text-white transition-colors duration-200">
                  Contact
                </Link>
              </li>
              <li>
                <a href="/careers" className="text-clinic-gray-300 hover:text-white transition-colors duration-200">
                  Careers
                </a>
              </li>
              <li>
                <a href="/privacy" className="text-clinic-gray-300 hover:text-white transition-colors duration-200">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms" className="text-clinic-gray-300 hover:text-white transition-colors duration-200">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="/hipaa" className="text-clinic-gray-300 hover:text-white transition-colors duration-200">
                  HIPAA Notice
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Contact Info</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-clinic-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">24/7 Crisis Line</p>
                  <p className="text-clinic-gray-300">(555) 123-4567</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-clinic-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Main Office</p>
                  <p className="text-clinic-gray-300">(555) 123-4568</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-clinic-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Email</p>
                  <p className="text-clinic-gray-300">info@ennvital.com</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-clinic-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Address</p>
                  <p className="text-clinic-gray-300">
                    123 Medical Center Dr<br />
                    Healthcare City, HC 12345
                  </p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-clinic-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Business Hours</p>
                  <p className="text-clinic-gray-300">
                    Mon-Fri: 8:00 AM - 6:00 PM<br />
                    Emergency: 24/7
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Emergency Notice */}
        <div className="border-t border-clinic-gray-700 pt-8 mb-8">
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Phone className="w-6 h-6 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-red-300 font-semibold">Mental Health Emergency?</p>
                <p className="text-red-200 text-sm">
                  If you're experiencing a mental health crisis, call our 24/7 crisis line at (555) 123-4567 
                  or contact emergency services at 911. Do not use this website for emergency situations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-clinic-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-clinic-gray-400 text-sm">
              © {currentYear} EnnVital Psychiatric Services. All rights reserved.
            </div>
            <div className="flex items-center space-x-6 text-sm text-clinic-gray-400">
              <a href="/accessibility" className="hover:text-white transition-colors duration-200">
                Accessibility
              </a>
              <a href="/sitemap" className="hover:text-white transition-colors duration-200">
                Sitemap
              </a>
              <a href="/quality-measures" className="hover:text-white transition-colors duration-200">
                Quality Measures
              </a>
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-green-400" />
                <span>SSL Secured</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};